import { type AxiosResponse } from 'axios'
import type { AxiosOptions, IResponseStatus } from 'shared/api'
import { baseApi } from 'entities/Auth'
import {
  type IResponseConversation,
  type IResponseConversations,
  type IResponseConversationsSearch,
  type IResponseConversationsFilter,
  type IResponseConversationsOrder,
  type IResponseExportConversation,
  type IParamsExportConversation,
  type IParamsConversationCreate,
  type IParamsConversationsList,
  type IParamsConversationsTeamsNumber,
  type IParamsConversationsSearch,
  type IParamsConversationsBatchQueueClose,
  type IParamsConversationsBatchQueueOpen,
  type IParamsConversationsBatchQueueRead,
  type IParamsConversationsBatchQueueUnread,
  type IParamsConversationsBatchQueueFavorite,
  type IParamsConversationsFindByContacts,
  type IParamsConversationsBatchQueueReassign,
  type IParamsConversationsMessagesMarkRead,
  type IParamsConversationsBatchQueueNeedResponse,
  type IParamsConversationsNeedResponseById,
  type IConversationsBatchQueueNeedResponseResponse,
  type IResponseConversationsTeamsNumber,
  type IResponseConversationsQuickFilter,
  type IParamsConversationState,
  type IResponseGetConversationsByIdStates,
  type IParamsGetConversationsByIdStates,
  type IResponseConversationError,
  type IParamsGroupForceCreate,
  type IResponseGroupForceCreate,
  type IParamsConversationsQuickFilter,
  type IParamsConversationsAllForContact,
  type IResponseConversationsAllForContact,
} from 'entities/Conversation/api/types'

class Api {
  getById(id: number, options?: AxiosOptions): Promise<AxiosResponse<IResponseConversation>> {
    return baseApi.get(`conversations/${id}`, { ...options })
  }

  getConversations(
    params: IParamsConversationsList,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseConversations>> {
    return baseApi.get('conversations', { params, ...options })
  }

  updateConversationsBatchQueueClose(
    params: IParamsConversationsBatchQueueClose
  ): Promise<AxiosResponse<string>> {
    return baseApi.put('conversations/batch-queue/close', params)
  }

  updateConversationsBatchQueueOpen(
    params: IParamsConversationsBatchQueueOpen
  ): Promise<AxiosResponse<string>> {
    return baseApi.put('conversations/batch-queue/open', params)
  }

  updateConversationsBatchQueueRead(
    params: IParamsConversationsBatchQueueRead
  ): Promise<AxiosResponse<string>> {
    return baseApi.put('conversations/batch-queue/read', params)
  }

  updateConversationsBatchQueueUnread(
    params: IParamsConversationsBatchQueueUnread
  ): Promise<AxiosResponse<string>> {
    return baseApi.put('conversations/batch-queue/unread', params)
  }

  updateConversationsBatchQueueFavorite(
    params: IParamsConversationsBatchQueueFavorite
  ): Promise<AxiosResponse<string>> {
    return baseApi.put('conversations/batch-queue/favorite', params)
  }

  getConversationsSearch(
    params: IParamsConversationsSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseConversationsSearch>> {
    return baseApi.get('conversations/search', { ...options, params })
  }

  getConversationsAllForContact(
    contact_id: number,
    params: IParamsConversationsAllForContact,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseConversationsAllForContact>> {
    return baseApi.get(`conversations/all-for-contact/${contact_id}`, { ...options, params })
  }

  getFiltersList(options?: AxiosOptions): Promise<AxiosResponse<IResponseConversationsFilter[]>> {
    return baseApi.get('conversations/filters/full-list', options)
  }

  getOrdersList(options?: AxiosOptions): Promise<AxiosResponse<IResponseConversationsOrder[]>> {
    return baseApi.get('conversations/orders/list', options)
  }

  create(
    params: IParamsConversationCreate,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseConversation, IResponseConversationError>> {
    return baseApi.post('conversations', params, options)
  }

  createGroupForce(
    params: IParamsGroupForceCreate,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGroupForceCreate>> {
    return baseApi.post('conversations/group/force-create', params, options)
  }

  updateByIdReassign(
    id: number,
    user_id: number | null
  ): Promise<AxiosResponse<IResponseConversation>> {
    return baseApi.post(`conversations/${id}/reassign`, { user_id })
  }

  updateByIdReassignBulk(
    params: IParamsConversationsBatchQueueReassign
  ): Promise<AxiosResponse<IResponseConversation>> {
    return baseApi.put('conversations/batch-queue/reassign', params)
  }

  updateByIdOpen(id: number): Promise<AxiosResponse<IResponseConversation>> {
    return baseApi.put(`conversations/${id}/open`)
  }

  updateByIdClose(id: number): Promise<AxiosResponse<IResponseConversation>> {
    return baseApi.put(`conversations/${id}/close`)
  }

  updateByIdExport({
    id,
    format,
    includeNotes,
  }: IParamsExportConversation): Promise<AxiosResponse<IResponseExportConversation>> {
    return baseApi.get(`conversations/${id}/export`, {
      params: {
        format,
        includeNotes,
      },
    })
  }

  updateByIdRead(id: number): Promise<AxiosResponse<IResponseConversation>> {
    return baseApi.put(`conversations/${id}/read`)
  }

  updateByIdUnread(id: number): Promise<AxiosResponse<IResponseConversation>> {
    return baseApi.put(`conversations/${id}/unread`)
  }

  updatePriority(conversation_id: number, priority: boolean) {
    return baseApi.post('conversations/favorite', { conversation_id, favorite: priority })
  }

  getTeamsNumber(
    params: IParamsConversationsTeamsNumber
  ): Promise<AxiosResponse<IResponseConversationsTeamsNumber>> {
    return baseApi.post('conversations/teams/number', params)
  }

  getConversationsFindByContacts(
    params: IParamsConversationsFindByContacts
  ): Promise<AxiosResponse<IResponseConversation | undefined>> {
    return baseApi.get('conversations/find-by-contacts', {
      params: params,
    })
  }

  updateMessagesByIdRead(
    conversation_id: number,
    params: IParamsConversationsMessagesMarkRead
  ): Promise<AxiosResponse> {
    return baseApi.patch(`conversations/${conversation_id}/messages/mark-read`, params)
  }

  updateConversationsBatchQueueNeedResponse(
    params: IParamsConversationsBatchQueueNeedResponse
  ): Promise<AxiosResponse<IConversationsBatchQueueNeedResponseResponse>> {
    return baseApi.put('conversations/batch-queue/need_response', params)
  }

  updateConversationsNeedResponseById(
    id: number,
    params: IParamsConversationsNeedResponseById
  ): Promise<AxiosResponse<IResponseConversation>> {
    return baseApi.put(`conversations/${id}/need_response`, params)
  }

  getConversationsQuickFilters(
    params: IParamsConversationsQuickFilter
  ): Promise<AxiosResponse<IResponseConversationsQuickFilter[]>> {
    return baseApi.get('conversations/filters/counter-list', {
      params: params,
    })
  }

  changeConversationStateById(
    id: number,
    params: IParamsConversationState
  ): Promise<AxiosResponse<IResponseStatus>> {
    const { state, ...rest } = params
    return baseApi.post(`conversations/${id}/${state}`, rest)
  }

  getConversationsByIdStates(
    id: number,
    params: IParamsGetConversationsByIdStates,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetConversationsByIdStates>> {
    return baseApi.get(`conversations/${id}/states`, {
      params: params,
      ...options,
    })
  }
}

export const ConversationsApi = new Api()
